import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import styles from './ChargesTab.module.scss';
import DateFormat from 'common/components/dateFormat/dateFormat';
import { DATE_FORMAT_DEFAUT, DefaultAssetImage, DEFAULT_PAGINATION_ITEMS_PER_PAGE, MAX_PAGINATION_ITEMS_PER_PAGE, LOGGER_LOG_TYPE } from 'Config';
import Loading from 'common/services/Loading';
import Logger from 'common/services/Logger';
import { useToasts } from 'react-toast-notifications';
import ChargesService from 'api/charges/ChargesService';
import { ChargesSearchCriteria } from 'api/charges/models/ChargesSearchCriteria';
import { ChargeCounterListDto, ChargeDto } from 'api/charges/models/ChargeDto';
import AvatarWithText from 'common/components/avatar/AvatarWithText';
import ReactTooltip from 'react-tooltip';
import { UserProfile } from 'api/account/models/UserProfile';
import MoneyFormat from 'common/components/moneyFormat/MoneyFormat';
import { useSelector } from 'react-redux';
import { Reducers } from 'store/types';
import DeletedVehicleCircle from 'common/components/deletedVehicleCircle/DeletedVehicleCircle';
import { useHistory } from 'react-router-dom';
import PercentageFormat from 'common/components/percentageFormat/PercentageFormat';
import CarIcon from 'assets/svg/car.svg';
import CoinsIcon from 'assets/svg/coins.svg';
import MediaIcon from 'assets/svg/media.svg';
import ChargeFiltersScreen from 'screens/charges/components/ChargeFiltersScreen';
import ScreenHeaderButton from 'common/components/screenHeader/ScreenHeaderButton';
import FiltersIcon from 'assets/svg/desktop_filtres.svg';
import Popover from 'common/components/popover/Popover';
import { GenericType } from 'api/genericTypes/enums/GenericType';
import RenderSVG from 'common/components/renderSVG/RenderSVG';
import DataTableWithSidebar from 'common/components/dataTable/DataTableWithSidebar';
import { DataTableColumn, OrderData } from 'common/components/dataTable/models';
import useBidirectionalScroll from 'common/hooks/useBidirectionalScroll';
import FilterBadgesOverflow, { FilterBadgeItem } from 'common/components/filterBadgesOverflow/FilterBadgesOverflow';
import InfoCard from 'common/components/infoCard/InfoCard';
import { compactPageLayoutStyles } from 'common/components/compactPageLayout';
import ExportListingButton from 'common/components/exportListingButton/ExportListingButton';
import colors from 'styles/export/colors.module.scss';

interface Filters {
    page?: number;
    scrollToItemId?: string;

    startDate?: Date | null;
    endDate?: Date | null;
    vehicleId?: string;
    vehicleName?: string;
    typeId?: string;
    typeName?: string;
    type?: string;
    supplierId?: string;
    supplierName?: string;
    responsibleId?: string;
    responsibleName?: string;
    cost?: number[];
    refundState?: string;
    refundStateName?: string;
    refundSituation?: string;
    vehiclesInactive?: boolean;
    fuelId?: string;
    fuelName?: string;
    orderBy?: string;
    orderColumn?: string;
}


export type Props = {
    geofenceId: string | undefined;
    onNavigate?: () => void;
} & React.DetailedHTMLProps<React.LabelHTMLAttributes<HTMLDivElement>, HTMLDivElement>;

const INFO_CARD_COLORS = [
    colors.counterTeal1,
    colors.counterTeal2,
    colors.counterBlue1,
];

const ChargesTab = ({ geofenceId, onNavigate }: Props) => {
    const { t } = useTranslation();
    const { addToast } = useToasts();
    const userProfile = useSelector<Reducers, UserProfile | null>(state => state.authentication.profile);
    const history = useHistory();
    const [filters, setFilters] = useState<Filters>({});
    const [filtersTotal, setFiltersTotal] = useState(0);
    const [criteria, setCriteria] = useState<ChargesSearchCriteria>({
        itemsPerPage: DEFAULT_PAGINATION_ITEMS_PER_PAGE,
        page: 1,
        orderBy: 'desc',
        orderColumn: 'c.date',
        geofenceId: geofenceId,
        translations: {},
        fuelTypeTranslations: {},
        isFromGeofencePage: true
    });
    const [counters, setCounters] = useState<ChargeCounterListDto>();
    const imageCacheKey = useRef(new Date().getTime());
    const exportListingRef = useRef<{ exportExcel: () => Promise<void> } | null>(null);

    const filterJsonKey = `GEOFENCECHARGESFILTER-${geofenceId}`;
    const isFirstLoad = useRef(true);
    const skipCounters = useRef(false);
    const isLoadingData = useRef(false);

    // Infinite scroll state
    const [order, setOrder] = useState<OrderData<ChargeDto>>({
        field: 'date' as keyof ChargeDto,
        orderColumn: 'c.date',
        isOrderAsc: false,
    });
    const getFieldFromOrderColumn = (oc: string): keyof ChargeDto => {
        const mapping: Record<string, string> = {
            'vehicle.registration_number': 'vehicleRegistrationNumber',
            'vb.name': 'vehicleBrandName',
            'vm.name': 'vehicleModelName',
            'responsible.real_name': 'responsibleName',
            'c.date': 'date',
            'translated_type_name': 'typeName',
            'supplier.name': 'supplierName',
            'c.cost': 'cost',
            'c.payment_date': 'paymentDate',
            'geof.geofences_cost': 'geofencesCost',
        };
        return (mapping[oc] ?? 'date') as keyof ChargeDto;
    };

    const [charges, setCharges] = useState<ChargeDto[]>([]);
    const [totalItems, setTotalItems] = useState(0);
    const [isLoadingMore, setIsLoadingMore] = useState(false);

    const {
        loadedPageRange,
        isLoadingPrevious,
        scrollToRowId: scrollToItemId,
        shouldLoadAdjacentPages,
        setLoadedPageRange,
        setIsLoadingPrevious,
        resetScrollState,
        calculatePageFromIndex,
        prepareLoadPrevious,
        adjustScrollAfterPrepend,
        finishRestoration,
        initiateRestoration,
        getHasMore,
        getHasMorePrevious,
        getOnLoadMore,
        getOnLoadPrevious,
    } = useBidirectionalScroll({
        cacheKey: filterJsonKey,
        itemsPerPage: DEFAULT_PAGINATION_ITEMS_PER_PAGE,
    });

    useEffect(() => {
        if (isFirstLoad.current == true) {
            loadFilters()
        } else {
            const isLoadMore = criteria.page > loadedPageRange.max && charges.length > 0;
            getData(isLoadMore);
        }
    }, [criteria]);

    // Adjust scroll position after prepending items
    useEffect(() => {
        if (!isLoadingPrevious) {
            adjustScrollAfterPrepend();
        }
    }, [charges, isLoadingPrevious, adjustScrollAfterPrepend]);

    const getData = async (isLoadMore = false) => {
        if (isLoadingData.current) return;
        isLoadingData.current = true;

        try {
            if (!isLoadMore) {
                Loading.show();
            } else {
                setIsLoadingMore(true);
            }

            const translations = t('common.generic_types', { returnObjects: true });
            const fuelTypeTranslations = t('vehicle.fuels', { returnObjects: true });

            const [chargesResult, countersDB] = await Promise.all([
                ChargesService.getList({ ...criteria, translations, fuelTypeTranslations }),
                skipCounters.current ? null : geofenceId ? ChargesService.getGeofencesChargesCountersList({ ...criteria, translations, fuelTypeTranslations }) : ChargesService.getCountersList({ ...criteria, translations, fuelTypeTranslations })
            ]);

            if (isLoadMore) {
                setCharges(prev => [...prev, ...chargesResult.items]);
                setLoadedPageRange(prev => ({ ...prev, max: criteria.page }));
            } else {
                setCharges(chargesResult.items);
            }
            setTotalItems(chargesResult.totalItems);

            if (countersDB) {
                setCounters(countersDB);
            }

            // Handle scroll restoration
            if (shouldLoadAdjacentPages.current) {
                shouldLoadAdjacentPages.current = false;
                const currentPage = criteria.page;
                const totalPages = Math.ceil(chargesResult.totalItems / (criteria.itemsPerPage || DEFAULT_PAGINATION_ITEMS_PER_PAGE));

                if (currentPage > 1) {
                    try {
                        const prevCriteria = { ...criteria, page: currentPage - 1, translations, fuelTypeTranslations };
                        const prevResult = await ChargesService.getList(prevCriteria);
                        setCharges(prev => [...prevResult.items, ...prev]);
                        setLoadedPageRange(prev => ({ ...prev, min: currentPage - 1 }));
                    } catch (e) {
                        Logger.error(LOGGER_LOG_TYPE.CHARGES, `Couldn't load previous page`, e);
                    }
                }

                if (currentPage < totalPages) {
                    try {
                        const nextCriteria = { ...criteria, page: currentPage + 1, translations, fuelTypeTranslations };
                        const nextResult = await ChargesService.getList(nextCriteria);
                        setCharges(prev => [...prev, ...nextResult.items]);
                        setLoadedPageRange(prev => ({ ...prev, max: currentPage + 1 }));
                    } catch (e) {
                        Logger.error(LOGGER_LOG_TYPE.CHARGES, `Couldn't load next page`, e);
                    }
                }

                finishRestoration();
            }

        } catch (error) {
            Logger.error(LOGGER_LOG_TYPE.CHARGES, `Couldn't get charge list`, error);
            addToast(t('common.messages.error_load_info'), { appearance: 'error' });
            resetScrollState();
        } finally {
            skipCounters.current = true;
            Loading.hide();
            setIsLoadingMore(false);
            isLoadingData.current = false;
        }
    }

    // Infinite scroll load more
    const loadMore = useCallback(() => {
        if (isLoadingData.current || isLoadingMore || charges.length >= totalItems) return;
        const nextPage = loadedPageRange.max + 1;
        setCriteria(prev => ({ ...prev, page: nextPage }));
    }, [isLoadingMore, charges.length, totalItems, loadedPageRange.max]);

    // Load previous pages (bidirectional scroll)
    const loadPrevious = useCallback(async () => {
        if (isLoadingPrevious || loadedPageRange.min <= 1) return;

        setIsLoadingPrevious(true);
        const prevPage = loadedPageRange.min - 1;

        try {
            prepareLoadPrevious();
            const translations = t('common.generic_types', { returnObjects: true });
            const fuelTypeTranslations = t('vehicle.fuels', { returnObjects: true });
            const prevCriteria: ChargesSearchCriteria = {
                ...criteria,
                page: prevPage,
                itemsPerPage: DEFAULT_PAGINATION_ITEMS_PER_PAGE,
                translations,
                fuelTypeTranslations,
            };
            const result = await ChargesService.getList(prevCriteria);

            setCharges(prev => [...result.items, ...prev]);
            setLoadedPageRange(prev => ({ ...prev, min: prevPage }));
        } catch (error) {
            Logger.error(LOGGER_LOG_TYPE.CHARGES, `Couldn't get previous charges page`, error);
            addToast(t('common.messages.error_load_info'), { appearance: 'error' });
        } finally {
            setIsLoadingPrevious(false);
        }
    }, [isLoadingPrevious, loadedPageRange.min, criteria, addToast, t]);

    // Order handler for DataTableWithSidebar
    const handleOrderChange = (orderData: OrderData<ChargeDto>) => {
        setOrder(orderData);
        setCharges([]);
        resetScrollState();
        setTotalItems(0);
        skipCounters.current = true;
        setCriteria(prev => ({
            ...prev,
            page: 1,
            orderBy: orderData.isOrderAsc ? 'asc' : 'desc',
            orderColumn: orderData.orderColumn || orderData.field as string,
        }));
    };

    const loadFilters = () => {
        isFirstLoad.current = false;

        const cachedFilters = localStorage.getItem(filterJsonKey);
        if (cachedFilters != null) {
            const f: Filters = JSON.parse(cachedFilters) as Filters;

            // Handle scroll restoration
            if (f.scrollToItemId) {
                const startPage = initiateRestoration({
                    page: f.page || 1,
                    scrollToItemId: f.scrollToItemId,
                });

                const cleanedFilters = { ...f, scrollToItemId: undefined, page: 1 };
                localStorage.setItem(filterJsonKey, JSON.stringify(cleanedFilters));
                setFilters(cleanedFilters);
                updateTotalFilters(cleanedFilters);

                setCriteria(prev => ({
                    ...prev,
                    startDate: f.startDate,
                    endDate: f.endDate,
                    vehicleId: f.vehicleId,
                    typeId: f.typeId,
                    fuelId: f.fuelId,
                    supplierId: f.supplierId,
                    responsibleId: f.responsibleId,
                    minCost: f.cost && f.cost[0] != undefined ? f.cost[0] : undefined,
                    maxCost: f.cost && f.cost[1] != undefined ? f.cost[1] : undefined,
                    refundSituation: f.refundSituation,
                    refundState: f.refundState,
                    vehiclesInactive: f.vehiclesInactive,
                    page: startPage,
                    orderBy: f.orderBy,
                    orderColumn: f.orderColumn
                }));

                if (f.orderBy && f.orderColumn) {
                    setOrder({
                        field: getFieldFromOrderColumn(f.orderColumn),
                        orderColumn: f.orderColumn,
                        isOrderAsc: f.orderBy === 'asc',
                    });
                }

                return;
            }

            onChangeFilters(f, false, true);
        }
        else { getData() }
    }

    const renderTypeCell = (row: ChargeDto) => {
        return (
            <div className={styles.cellContainer}>
                <div className={styles.rowFlex}>
                    {row.typeIconSvg && <RenderSVG item={row.typeIconSvg} className={styles.divSVG} />}
                    {(row.typeName != GenericType.FUEL || !row.fuelName) && <span>{!row.typeIsSystem ? row.typeName : t('common.generic_types.' + row.typeName as any)}</span>}
                    {row.typeName == GenericType.FUEL && row.fuelName && <span>{t('vehicle.fuels.' + row.fuelName as any)}</span>}
                </div>
            </div>
        )
    };

    // DataTableWithSidebar columns
    const dataTableColumns: DataTableColumn<ChargeDto>[] = useMemo(() => {
        const cols: DataTableColumn<ChargeDto>[] = [
            {
                field: 'vehiclePhotoUrl' as keyof ChargeDto,
                name: t('common.image') as string,
                hideOrderButton: true,
                width: 80,
                hideOn: ['sm', 'md'],
                excludeFromExport: true,
                renderCell: row => (
                    <div className={styles.imageDescCell}>
                        <div className={styles.contentImage}>
                            <div className={styles.img} style={{ width: '5rem', height: '3rem', backgroundImage: row?.vehiclePhotoUrl ? 'url(' + row?.vehiclePhotoUrl + '?_=' + imageCacheKey.current + ')' : row?.vehicleSegmentDefaultPhotoUrl ? 'url(' + row?.vehicleSegmentDefaultPhotoUrl + '?_=' + imageCacheKey.current + ')' : 'url(' + DefaultAssetImage + ')' }} />
                        </div>
                    </div>
                ),
            },
            {
                field: 'vehicleRegistrationNumber' as keyof ChargeDto,
                name: t('charges.list.vehicle') as string,
                orderColumn: 'vehicle.registration_number',
                width: 150,
                renderCell: row => (
                    <div className={styles.descCell}>
                        <div className={styles.columnPlate}>
                            {row.vehicleRegistrationNumber}
                            {!row.vehicleActive && <DeletedVehicleCircle id={row.id ?? ''} />}
                        </div>
                    </div>
                ),
            },
            {
                field: 'vehicleBrandName' as keyof ChargeDto,
                name: t('vehicle.brand') as string,
                orderColumn: 'vb.name',
                width: 140,
                hideOn: ['sm', 'md', 'lg'],
                renderCell: row => <>{row.vehicleBrandName || '-'}</>,
            },
            {
                field: 'vehicleModelName' as keyof ChargeDto,
                name: t('vehicle.model') as string,
                orderColumn: 'vm.name',
                width: 140,
                hideOn: ['sm', 'md', 'lg'],
                renderCell: row => <>{row.vehicleModelName || '-'}</>,
            },
            {
                field: 'responsibleName' as keyof ChargeDto,
                name: t('common.responsible') as string,
                orderColumn: 'responsible.real_name',
                width: 180,
                hideOn: ['sm'],
                exportValue: row => row.responsibleName ?? '',
                renderCell: row => (
                    <AvatarWithText src={row.responsiblePhotoUrl}
                        size="x2"
                        hideAvatar={!row.responsibleName}><div className={styles.bold}>{row.responsibleName || '-'}</div></AvatarWithText>
                ),
            },
            {
                field: 'date' as keyof ChargeDto,
                name: t('charges.charge_date') as string,
                orderColumn: 'c.date',
                width: 140,
                hideOn: ['sm'],
                exportValue: row => row.date ? moment(row.date).format(DATE_FORMAT_DEFAUT) : '',
                renderCell: row => <DateFormat value={row.date} />,
            },
            {
                field: 'typeName' as keyof ChargeDto,
                name: t('charges.list.type') as string,
                orderColumn: 'translated_type_name',
                width: 150,
                hideOn: ['sm', 'md'],
                exportValue: row => {
                    if (row.typeName === GenericType.FUEL && row.fuelName) return t('vehicle.fuels.' + row.fuelName as any) as string;
                    return !row.typeIsSystem ? (row.typeName ?? '') : t('common.generic_types.' + row.typeName as any) as string;
                },
                renderCell: renderTypeCell,
            },
            {
                field: 'supplierName' as keyof ChargeDto,
                name: t('common.supplier') as string,
                orderColumn: 'supplier.name',
                width: 140,
                hideOn: ['sm', 'md', 'lg', 'xl'],
                exportValue: row => row.supplierName ?? '',
                renderCell: row => <>
                    <div data-tip={row.supplierUrl ? (row.supplierName || '-') : undefined}>
                        {row.supplierUrl && <img src={row.supplierUrl} style={{ height: '2.5rem' }}></img>}
                        {!row.supplierUrl && (row.supplierName || '-')}
                    </div>
                    <ReactTooltip />
                </>,
            },
            {
                field: 'cost' as keyof ChargeDto,
                name: t('common.cost') as string,
                orderColumn: 'c.cost',
                width: 120,
                headerNote: `${t('common.unit')}: ${t('common.euro')}`,
                renderCell: row => (
                    <div className={styles.costColumn}>
                        <div className={styles.moneyColumn}>
                            <MoneyFormat fixedDecimalScale={true} value={row.cost} />
                        </div>
                    </div>
                ),
            },
            {
                field: 'paymentDate' as keyof ChargeDto,
                name: t('charges.payment_date') as string,
                orderColumn: 'c.payment_date',
                width: 140,
                hideOn: ['sm', 'md', 'lg'],
                exportValue: row => row.paymentDate ? moment(row.paymentDate).format(DATE_FORMAT_DEFAUT) : '',
                renderCell: row => row.paymentDate ? <DateFormat value={row.paymentDate} format={DATE_FORMAT_DEFAUT} /> : <>-</>,
            },
            {
                field: 'geofencesCost' as keyof ChargeDto,
                name: t('charges.geofences.cost_center_value') as string,
                orderColumn: 'geof.geofences_cost',
                width: 200,
                cellAlignment: 'right',
                columnCellAlignment: 'right',
                headerNote: `${t('common.unit')}: ${t('common.euro')}`,
                renderCell: row => (
                    <div className={styles.costColumn}>
                        <span className={styles.percentageInline}>
                            <PercentageFormat fixedDecimalScale={true} value={row.geofencesPercentage} />
                        </span>
                        <MoneyFormat className={styles.moneyColumn}
                            fixedDecimalScale={true}
                            value={row.geofencesCost}
                        />
                    </div>
                ),
            },
        ];

        return cols;
    }, [t]);

    const onRowClick = (event: any, id?: string) => {
        if (id) {
            event.preventDefault();
            event.stopPropagation();

            const rowIndex = charges.findIndex(c => c.id === id);
            const actualPage = rowIndex >= 0 ? calculatePageFromIndex(rowIndex) : criteria.page;
            const newFilters = { ...filters, scrollToItemId: id, page: actualPage };
            newFilters.orderBy = criteria.orderBy;
            newFilters.orderColumn = criteria.orderColumn;

            saveFiltersOnCache(newFilters);

            if (onNavigate) {
                onNavigate();
            }

            if (event.ctrlKey) {
                window.open('/charges/details/' + id, '_blank');
                return;
            }
            history.push(`/charges/details/${id}`);
        }
    }

    const onChangeFilters = (f: Filters, skipCountersProp: boolean, isOnLoad?: boolean) => {
        updateTotalFilters(f);
        saveFiltersOnCache(f);

        if (!isOnLoad) {
            setCharges([]);
            resetScrollState();
            setTotalItems(0);
        }

        setCriteria(prev => ({
            ...prev,
            startDate: f.startDate,
            endDate: f.endDate,
            vehicleId: f.vehicleId,
            typeId: f.typeId,
            fuelId: f.fuelId,
            supplierId: f.supplierId,
            responsibleId: f.responsibleId,
            minCost: f.cost && f.cost[0],
            maxCost: f.cost && f.cost[1],
            refundSituation: f.refundSituation,
            refundState: f.refundState,
            vehiclesInactive: f.vehiclesInactive,
            page: (isOnLoad && f.page) ? f.page : 1,
            orderBy: f.orderBy || prev.orderBy || 'desc',
            orderColumn: f.orderColumn || prev.orderColumn || 'c.date'
        }))

        setOrder({
            field: getFieldFromOrderColumn(f.orderColumn || 'c.date'),
            orderColumn: f.orderColumn || 'c.date',
            isOrderAsc: (f.orderBy || 'desc') === 'asc',
        });

        skipCounters.current = skipCountersProp;
    }

    const saveFiltersOnCache = (f: Filters) => {
        setFilters(prev => ({ ...prev, ...f }))
        localStorage.setItem(filterJsonKey, JSON.stringify(f));
    }

    const updateTotalFilters = (f: Filters) => {
        let total = 0;

        if (f.startDate || f.endDate) {
            total++;
        }
        if (f.vehicleId && f.vehicleId.length > 0) {
            total++;
        }
        if (f.typeId && f.typeId.length > 0) {
            total++;
        }
        if (f.fuelId && f.fuelId.length > 0) {
            total++;
        }
        if (f.supplierId && f.supplierId.length > 0) {
            total++;
        }
        if (f.responsibleId && f.responsibleId.length > 0) {
            total++;
        }
        if (f.refundSituation != null) {
            total++;
        }

        if (f.refundState) {
            total++;
        }
        if (f.cost && (f.cost[0] > 0 || f.cost[1] > 0)) {
            total++;
        }
        if (f.vehiclesInactive) {
            total++;
        }

        setFiltersTotal(total);
    }

    const updateFilters = (items: { key: string; value: any }[]) => {
        const newFilters = JSON.parse(JSON.stringify(filters))
        items.forEach((item: { key: string; value: any }) => {
            newFilters[item.key] = item.value
        })

        onChangeFilters(newFilters, false)
    }

    // FilterBadgesOverflow items
    const filterItems = useMemo<FilterBadgeItem[]>(() => {
        const items: FilterBadgeItem[] = [];

        if (filters.vehicleName) {
            items.push({
                key: 'vehicle',
                label: t('vehicle.vehicle') as string,
                value: filters.vehicleName,
                onRemove: () => updateFilters([{ key: 'vehicleId', value: undefined }, { key: 'vehicleName', value: undefined }])
            });
        }

        if (filters.typeName) {
            items.push({
                key: 'type',
                label: t('common.type') as string,
                value: filters.typeName,
                onRemove: () => updateFilters([{ key: 'typeId', value: undefined }, { key: 'typeName', value: undefined }])
            });
        }

        if (filters.fuelName) {
            items.push({
                key: 'fuel',
                label: t('vehicle.fuel') as string,
                value: filters.fuelName,
                onRemove: () => updateFilters([{ key: 'fuelId', value: undefined }, { key: 'fuelName', value: undefined }])
            });
        }

        if (filters.refundState != null) {
            items.push({
                key: 'refundState',
                label: t('charges.reimbursement_status') as string,
                value: t(('charges.list.request_status_type.' + filters.refundState) as any) as string,
                onRemove: () => updateFilters([{ key: 'refundState', value: undefined }])
            });
        }

        if (filters.refundSituation != null) {
            items.push({
                key: 'refundSituation',
                label: t('charges.reimbursement_situation') as string,
                value: t(('charges.situations.' + filters.refundSituation) as any) as string,
                onRemove: () => updateFilters([{ key: 'refundSituation', value: undefined }])
            });
        }

        if (filters.responsibleName) {
            items.push({
                key: 'responsible',
                label: t('common.responsible') as string,
                value: filters.responsibleName,
                onRemove: () => updateFilters([{ key: 'responsibleId', value: undefined }, { key: 'responsibleName', value: undefined }])
            });
        }

        if (filters.supplierName) {
            items.push({
                key: 'supplier',
                label: t('common.supplier') as string,
                value: filters.supplierName,
                onRemove: () => updateFilters([{ key: 'supplierId', value: undefined }, { key: 'supplierName', value: undefined }])
            });
        }

        if (filters.cost?.filter(x => x !== undefined).length) {
            let value = '';
            if (filters.cost.filter(i => i !== undefined && i !== null).length === 2) {
                value = `${filters.cost[0]} - ${filters.cost[1]}`;
            } else if (filters.cost[0] !== undefined && filters.cost[0] !== null) {
                value = `> ${filters.cost[0]}`;
            } else {
                value = `< ${filters.cost[1]}`;
            }
            items.push({
                key: 'cost',
                label: t('common.cost') as string,
                value,
                onRemove: () => updateFilters([{ key: 'cost', value: undefined }])
            });
        }

        if (filters.startDate || filters.endDate) {
            let value = '';
            if (filters.startDate && filters.endDate) {
                value = `${new Date(filters.startDate).toLocaleDateString()} - ${new Date(filters.endDate).toLocaleDateString()}`;
            } else if (filters.startDate) {
                value = `${t('common.range.from')} ${new Date(filters.startDate).toLocaleDateString()}`;
            } else if (filters.endDate) {
                value = `${t('common.range.to')} ${new Date(filters.endDate).toLocaleDateString()}`;
            }
            items.push({
                key: 'dates',
                label: t('charges.filter_date') as string,
                value,
                onRemove: () => updateFilters([{ key: 'startDate', value: undefined }, { key: 'endDate', value: undefined }])
            });
        }

        if (filters.vehiclesInactive) {
            items.push({
                key: 'inactive',
                label: t('charges.vehicle_inactives') as string,
                value: t('common.yes') as string,
                onRemove: () => updateFilters([{ key: 'vehiclesInactive', value: undefined }])
            });
        }

        return items;
    }, [filters, t]);

    const renderCounterBoxes = () => (
        <div className={compactPageLayoutStyles.rowCounters}>
            {counters && <InfoCard
                label={t('charges.list.average_cost_per_month') as string}
                value={counters.averageCostPerMonth ?? 0}
                icon={MediaIcon}
                backgroundColor={INFO_CARD_COLORS[0]}
                isMoney
            />}
            {counters && <InfoCard
                label={t('charges.list.average_cost_of_charge_per_car') as string}
                value={counters.averageCostChargePerCar ?? 0}
                icon={CarIcon}
                backgroundColor={INFO_CARD_COLORS[1]}
                isMoney
            />}
            {counters && <InfoCard
                label={t('charges.list.cumulative_total_cost') as string}
                value={counters.cumulativeTotalCost ?? 0}
                icon={CoinsIcon}
                backgroundColor={INFO_CARD_COLORS[2]}
                isMoney
            />}
        </div>
    );

    const fetchAllForExport = useCallback(async () => {
        const translations = t('common.generic_types', { returnObjects: true });
        const fuelTypeTranslations = t('vehicle.fuels', { returnObjects: true });
        const result = await ChargesService.getList({
            ...criteria,
            page: 1,
            itemsPerPage: MAX_PAGINATION_ITEMS_PER_PAGE,
            translations,
            fuelTypeTranslations,
        });
        return result.items;
    }, [criteria]);

    return (
        <div className={styles.tabWrapper}>
            <div className={styles.headerRow}>
                <FilterBadgesOverflow items={filterItems} align="right" />
                <Popover
                    contentContainerClassName={styles.filtersPopoverContainer}
                    containerStyle={{ boxShadow: '0 4px 14px rgba(0, 0, 0, 0.1)', backgroundColor: '#fff' }}
                    onClickOutside={() => updateTotalFilters(filters)}
                    content={setIsPopoverOpen => <ChargeFiltersScreen
                        isVehicle={false}
                        isDriver={false}
                        filters={filters}
                        onFilter={f => { setIsPopoverOpen(false); onChangeFilters(f, false) }}
                        onChange={f => updateTotalFilters(f)}
                        hideGeoFenceFilters={true}
                    />}>
                    {(isPopoverOpen, setIsPopoverOpen) => (
                        <ScreenHeaderButton icon={FiltersIcon} onClick={() => setIsPopoverOpen(!isPopoverOpen)}>
                            {filtersTotal > 0 &&
                                <div className={styles.counterList}> <div className={styles.counterNumber}>{filtersTotal}</div> </div>
                            }
                        </ScreenHeaderButton>
                    )}
                </Popover>
                <ExportListingButton exportRef={exportListingRef} />
            </div>

            {counters && (
                <div className={compactPageLayoutStyles.countersSection}>
                    {renderCounterBoxes()}
                </div>
            )}

            <div className={compactPageLayoutStyles.listTblWrapper}>
                {isFirstLoad.current === false && (
                    <DataTableWithSidebar
                        cacheKey={`GEOFENCE_CHARGES_TABLE_${geofenceId}`}
                        columns={dataTableColumns}
                        rows={charges}
                        defaultOrder={order}
                        onOrderChange={handleOrderChange}
                        onRowClick={(row, _index, event) => onRowClick(event, row.id)}
                        onHref={row => `charges/details/${row.id}`}
                        onRowId={row => row.id ?? ''}
                        scrollToRowId={scrollToItemId}
                        infiniteScroll={{
                            isLoading: isLoadingMore,
                            hasMore: getHasMore(charges.length, totalItems),
                            onLoadMore: getOnLoadMore(loadMore),
                            hasMorePrevious: getHasMorePrevious(),
                            isLoadingPrevious,
                            onLoadPrevious: getOnLoadPrevious(loadPrevious),
                        }}
                        exportExcel={{
                            fetchAll: fetchAllForExport,
                            fileName: t('charges.title') as string,
                        }}
                        exportRef={exportListingRef}
                        hideExportInSidebar={true}
                    />
                )}
            </div>
        </div>
    );
}

export default ChargesTab;
